import { useState } from "react";
import { useParams } from "react-router-dom";

import { IWorkOrderDetails } from "core/types/entities/IWorkOrder";
import { useBaseAction, useEntityManagement, useHandleModals } from "hooks/core";
import { API } from "constants/RestApi";
import useSubWOAddEdit from "./useSubWOAddEdit";

const actions = ["subWO", "signature", "delete", "complete", "unComplete"] as const;

const useWorkOrderDetails = () => {
  const { id } = useParams();
  const [detailsLoader, setDetailsLoader] = useState<boolean>(false);
  const { request } = useBaseAction();
  const {
    modals: modalsWo,
    toggleModal: toggleWO,
    selectedEntity,
  } = useHandleModals<IWorkOrderDetails, typeof actions[number]>({
    modalsKeys: actions,
  });

  const {
    getByIdAction,
    createEditAction,
    selectedEntity: workOrder,
  } = useEntityManagement<IWorkOrderDetails>({
    baseUrl: API.WORK_ORDERS,
    onLoad: false,
    expand: `Company,subWorkOrders($expand=AssignedTechs($expand=Technician),
    contacts,expenses,equipment($expand=equipmentModel($expand=make($select=name,id),type($select=name);$select=model) ,notes($expand=files,createdBy) ;$select=name, serialNumber, lastServiceDate, id, equipmentModelId, isDeleted, companyId),
    vehicles,notes($expand=files,createdBy;$orderby=id desc)),notes($expand=files,createdBy)`,
  });

  const { getByIdAction: getQBO, selectedEntity: qbo } = useEntityManagement<IWorkOrderDetails>({
    baseUrl: API.WORK_ORDERS,
    onLoad: false,
    expand: `Estimate,Signature`,
  });

  const requestSignature = async (signagure: string, _workOrder: IWorkOrderDetails) => {
    await request({
      method: "PUT",
      url: API.WORK_ORDERS_SIGNATURE,
      body: {
        WorkOrderId: _workOrder.id,
        rowVersion: _workOrder.rowVersion,
        data: { root: signagure },
      },
      successMessage: "Item has been changed",
      useLoader: true,
    });
  };

  const requestToComplete = async (_workOrder: IWorkOrderDetails) => {
    await request({
      method: "PUT",
      url: API.WORK_ORDERS_COMPLETE,
      body: { id: _workOrder.id, rowVersion: _workOrder.rowVersion },
      successMessage: "Item has been changed",
      useLoader: true,
    });
  };

  const requestUnCompleteWO = async (_workOrder: IWorkOrderDetails) => {
    await request({
      method: "PUT",
      url: API.WORK_ORDERS_UnComplete,
      body: { id: _workOrder.id, rowVersion: _workOrder.rowVersion },
      successMessage: "Item has been uncompleted",
      useLoader: true,
    });
  };

  const requestDeleteSignature = async (_workOrder: IWorkOrderDetails) => {
    await request({
      method: "DELETE",
      url: API.WORK_ORDERS_SIGNATURE,
      body: { id: _workOrder.id, rowVersion: _workOrder.rowVersion },
      successMessage: "Item has been deleted",
      useLoader: true,
    });
  };

  const onSaveSignatureHandeler = async (signagure: string) => {
    if (signagure && workOrder) {
      await requestSignature(signagure, workOrder);
      await getQBO(workOrder.id, "id", false);
      if (id) {
        await getByIdAction(id, "id", false);
      }
    }
    toggleWO({ key: "signature", hideAll: true, selectedItem: null });
  };

  const onDeleteHandler = async () => {
    if (workOrder) {
      await requestDeleteSignature(workOrder);
      await getQBO(workOrder.id, "id", false);
      if (id) getByIdAction(id, "id", false);
    }
    toggleWO({ key: "subWO", hideAll: true, selectedItem: null });
  };

  const {
    contacts,
    users,
    vehicles,
    equipments,
    getEquipmentOptions,
    getContactsOptions,
    getVehicles,
    getUsers,
  } = useSubWOAddEdit();

  const getContacts = () => {
    if (workOrder?.companyId) {
      getContactsOptions(`companyId eq ${workOrder?.companyId}`, false);
    }
  };

  const getEquipments = () => {
    if (workOrder?.companyId) {
      getEquipmentOptions(`companyId eq ${workOrder?.companyId}`, false);
    }
  };

  const getWorkOrder = async (useLoader = true) => {
    if (id) {
      if (useLoader) setDetailsLoader(true);
      await getByIdAction(id, "id", false);
      if (useLoader) setDetailsLoader(false);
    }
  };

  const onCompleteHandler = async () => {
    if (workOrder) {
      await requestToComplete(workOrder);
    }
    await getWorkOrder();
    toggleWO({ key: "complete" });
  };

  const onUnCompleteWOHandler = async () => {
    if (workOrder) {
      await requestUnCompleteWO(workOrder);
    }
    await getWorkOrder();
    toggleWO({ key: "unComplete" });
  };

  const onSaveSubWOHandler = async () => {
    await getWorkOrder();
    toggleWO({ key: "subWO" });
  };

  return {
    getByIdAction,
    createEditAction,
    workOrder,
    toggleWO,
    modalsWo,
    contacts,
    users,
    vehicles,
    equipments,
    detailsLoader,
    setDetailsLoader,
    onSaveSignatureHandeler,
    selectedEntity,
    onDeleteHandler,
    getQBO,
    qbo,
    getContacts,
    getEquipments,
    getVehicles,
    getUsers,
    getWorkOrder,
    onSaveSubWOHandler,
    onCompleteHandler,
    onUnCompleteWOHandler,
  };
};

export default useWorkOrderDetails;
